"use client";

/**
 * Header user menu — appears at the right of the desktop header.
 *
 * Client component (must be — the header is also mounted from `error.tsx`,
 * which is a "use client" file, so anything downstream of Header has to be
 * client-tree-safe; importing src/lib/auth/server.ts here would break the
 * client bundle).
 *
 * Reads the current session via the Supabase browser client + subscribes to
 * onAuthStateChange so the menu flips between signed-in and signed-out states
 * the moment the user signs in/out (no full reload required).
 *
 *   signed out → "Sign in / Sign up" pair
 *   signed in  → email/name + dropdown (UserDropdown.tsx)
 *
 * Mobile users get the same options via MobileNav.tsx — this component is
 * desktop-only (hidden below md).
 */

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { createSupabaseBrowserClient } from "@/lib/auth/client";
import { accountIdentityLabel } from "@/lib/auth/phone";
import UserDropdown from "./UserDropdown";

interface ProfileRow {
  full_name: string | null;
}

export default function HeaderUserMenu() {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "anon" }
    | { kind: "user"; email: string; fullName: string | null }
  >({ kind: "loading" });
  const tc = useTranslations("common");
  const tu = useTranslations("userMenu");

  useEffect(() => {
    // If Supabase Auth env vars aren't configured yet, render the anonymous
    // state so the rest of the site still works (don't block on a missing
    // dep that isn't set up).
    if (
      !process.env.NEXT_PUBLIC_SUPABASE_URL ||
      !process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
    ) {
      setState({ kind: "anon" });
      return;
    }

    const supabase = createSupabaseBrowserClient();
    let cancelled = false;

    async function loadProfile(email: string, userId: string) {
      const { data } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", userId)
        .maybeSingle<ProfileRow>();
      if (!cancelled) {
        setState({ kind: "user", email, fullName: data?.full_name ?? null });
      }
    }

    supabase.auth.getUser().then(({ data: { user } }) => {
      if (cancelled) return;
      if (user?.email) loadProfile(user.email, user.id);
      else setState({ kind: "anon" });
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      const u = session?.user;
      if (u?.email) loadProfile(u.email, u.id);
      else setState({ kind: "anon" });
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (state.kind === "loading") {
    // Tiny placeholder block to avoid layout shift while we figure out who
    // they are. ~84px is roughly the "Sign in / Sign up" pair width.
    return <div className="hidden md:block w-[180px] h-9" aria-hidden="true" />;
  }

  if (state.kind === "anon") {
    return (
      <>
        {/* Desktop: full pair */}
        <div className="hidden md:flex items-center gap-2">
          <Link
            href="/sign-in"
            className="px-3.5 py-1.5 text-sm font-semibold text-ss-text hover:bg-ss-bg-soft rounded-full"
          >
            {tc("signIn")}
          </Link>
          <Link
            href="/sign-up"
            className="px-3.5 py-1.5 text-sm font-semibold bg-ss-blue text-white rounded-full hover:bg-ss-blue-dark"
          >
            {tc("signUp")}
          </Link>
        </div>
        {/* Mobile: compact "Sign in" pill — single CTA that fits next to the
            hamburger without crowding the row. Tapping it goes straight to
            /sign-in (which has a "Don't have an account? Sign up" link). The
            hamburger sheet ALSO has Sign in / Sign up entries, but this
            surfaces the action so guests don't have to discover the menu. */}
        {/* Mobile sign-in lives in the hamburger drawer (MobileNav) — the
            inline pill was widening the mobile header row (esp. the longer
            Arabic label) and pushing the menu/toggle off-screen, so it's
            hidden here. */}
      </>
    );
  }

  return (
    <>
      <div className="hidden md:block">
        <UserDropdown email={accountIdentityLabel(state.email)} fullName={state.fullName} />
      </div>
      {/* Mobile signed-in: small avatar circle linking to /account so the
          user can reach their profile without opening the hamburger. */}
      <Link
        href="/account"
        className="md:hidden w-9 h-9 grid place-items-center rounded-full bg-ss-blue text-white text-[13px] font-bold uppercase"
        aria-label={tu("yourAccount")}
      >
        {(state.fullName || state.email)?.charAt(0).toUpperCase() || "?"}
      </Link>
    </>
  );
}
